import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ChevronDown } from "lucide-react";
import Reveal from "@/components/Reveal";
import { REQUEST_TYPES, LOCATION_FORMATS } from "@/lib/site";

const API = process.env.REACT_APP_BACKEND_URL;

const INITIAL = {
  fullName: "",
  email: "",
  organization: "",
  requestType: REQUEST_TYPES[0],
  preferredDate: "",
  locationFormat: LOCATION_FORMATS[0],
  eventDetails: "",
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const labelCls = "block text-sm font-medium text-navy";
const inputCls =
  "mt-2 w-full bg-white border border-line-strong rounded-md px-4 py-3 text-[15px] text-ink placeholder:text-mist/70 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors";

export default function Booking() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [done, setDone] = useState(false);

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: "" });
  };

  const validate = () => {
    const next = {};
    if (!form.fullName.trim()) next.fullName = "Please share your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = "Please share a valid email address.";
    if (!form.eventDetails.trim())
      next.eventDetails = "Please tell us a little about the event.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/inquiries`, form, {
        headers: { "Content-Type": "application/json" },
      });
      if (res.data && res.data.ok) {
        setDone(true);
        window.__lenis
          ? window.__lenis.scrollTo(0, { immediate: true })
          : window.scrollTo(0, 0);
      } else {
        setServerError("Your inquiry could not be saved. Please try again.");
      }
    } catch (err) {
      setServerError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-ivory">
      <section className="mx-auto max-w-6xl px-5 sm:px-8 pt-36 pb-28 sm:pt-44">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                Booking
              </p>
              <h1 className="mt-5 font-serif text-4xl sm:text-5xl text-navy tracking-tight leading-[1.1]">
                Invite Chaplain Derrick.
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-stone2">
                Tell us who is gathering, what the day holds, and what your
                people are carrying. Every inquiry is read with care.
              </p>

              <div className="mt-10 space-y-5">
                {[
                  {
                    t: "Your note is saved and read.",
                  },
                  {
                    t: "Replies go to the email you shared, to talk through the event before anything is fixed.",
                  },
                  {
                    t: "Dates, format, and details are settled together.",
                  },
                ].map((s, i) => (
                  <div key={i} className="flex gap-4">
                    <span className="shrink-0 font-serif text-gold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-stone2 leading-relaxed">{s.t}</p>
                  </div>
                ))}
              </div>

              <p className="mt-10 text-xs leading-relaxed text-mist border-t border-line pt-6">
                Your inquiry is kept private and used to respond to your
                request. For now, this form is the best way to reach Chaplain
                Derrick.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              {done ? (
                <div
                  className="bg-white border border-line rounded-md p-10 sm:p-14 text-center"
                  data-testid="booking-success-message"
                  role="status"
                >
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold-light">
                    <CheckCircle2 className="w-8 h-8 text-gold-hover" />
                  </span>
                  <h2 className="mt-7 font-serif text-3xl text-navy">
                    Received, with thanks.
                  </h2>
                  <p className="mt-4 text-stone2 leading-relaxed max-w-md mx-auto">
                    Your inquiry has been saved and will be read with care.
                    Replies go to the email you shared — if nothing arrives,
                    please check your spam folder.
                  </p>
                  <Link
                    to="/"
                    data-testid="booking-success-home-link"
                    className="mt-9 inline-flex items-center gap-2 border border-navy/25 text-navy px-7 py-3 rounded-full text-sm font-medium hover:border-gold hover:text-gold-hover transition-colors"
                  >
                    Back to the book
                  </Link>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  noValidate
                  className="bg-white border border-line rounded-md p-8 sm:p-10 space-y-7"
                >
                  <div className="grid sm:grid-cols-2 gap-7">
                    <div>
                      <label htmlFor="bk-name" className={labelCls}>
                        Your name <span className="text-burgundy">*</span>
                      </label>
                      <input
                        id="bk-name"
                        type="text"
                        value={form.fullName}
                        onChange={set("fullName")}
                        data-testid="booking-input-name"
                        className={inputCls}
                        placeholder="First and last"
                      />
                      {errors.fullName && (
                        <p className="mt-2 text-xs text-burgundy" role="alert">
                          {errors.fullName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="bk-email" className={labelCls}>
                        Email address <span className="text-burgundy">*</span>
                      </label>
                      <input
                        id="bk-email"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        data-testid="booking-input-email"
                        className={inputCls}
                        placeholder="you@example.org"
                      />
                      {errors.email && (
                        <p className="mt-2 text-xs text-burgundy" role="alert">
                          {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="bk-org" className={labelCls}>
                      Organization or community
                    </label>
                    <input
                      id="bk-org"
                      type="text"
                      value={form.organization}
                      onChange={set("organization")}
                      data-testid="booking-input-organization"
                      className={inputCls}
                      placeholder="e.g., a hospice, a church, a caregiver network"
                    />
                  </div>

                  <div>
                    <label htmlFor="bk-type" className={labelCls}>
                      Type of request <span className="text-burgundy">*</span>
                    </label>
                    <div className="relative">
                      <select
                        id="bk-type"
                        value={form.requestType}
                        onChange={set("requestType")}
                        data-testid="booking-select-request-type"
                        className={`${inputCls} appearance-none pr-10`}
                      >
                        {REQUEST_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="w-4 h-4 text-mist absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-7">
                    <div>
                      <label htmlFor="bk-date" className={labelCls}>
                        Preferred date — or a rough idea
                      </label>
                      <input
                        id="bk-date"
                        type="text"
                        value={form.preferredDate}
                        onChange={set("preferredDate")}
                        data-testid="booking-input-date"
                        className={inputCls}
                        placeholder="e.g., early May, or 'this fall'"
                      />
                    </div>
                    <div>
                      <span className={labelCls}>
                        Location or format <span className="text-burgundy">*</span>
                      </span>
                      <div className="mt-3 flex flex-wrap gap-2.5">
                        {LOCATION_FORMATS.map((f) => (
                          <label
                            key={f}
                            className={`cursor-pointer text-sm px-4 py-2.5 rounded-full border transition-colors ${
                              form.locationFormat === f
                                ? "bg-navy text-ivory border-navy"
                                : "bg-white text-stone2 border-line-strong hover:border-gold"
                            }`}
                          >
                            <input
                              type="radio"
                              name="locationFormat"
                              value={f}
                              checked={form.locationFormat === f}
                              onChange={set("locationFormat")}
                              data-testid={`booking-format-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                              className="sr-only"
                            />
                            {f}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="bk-details" className={labelCls}>
                      Tell us about your audience and goals{" "}
                      <span className="text-burgundy">*</span>
                    </label>
                    <textarea
                      id="bk-details"
                      rows={5}
                      value={form.eventDetails}
                      onChange={set("eventDetails")}
                      data-testid="booking-textarea-details"
                      className={`${inputCls} resize-y`}
                      placeholder="Estimated attendance, the occasion, what you hope the visit will do, anything else we should know."
                    />
                    {errors.eventDetails && (
                      <p className="mt-2 text-xs text-burgundy" role="alert">
                        {errors.eventDetails}
                      </p>
                    )}
                  </div>

                  {serverError && (
                    <p
                      className="text-sm text-burgundy bg-burgundy-soft border border-burgundy/20 rounded-md px-4 py-3"
                      role="alert"
                      data-testid="booking-server-error"
                    >
                      {serverError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="booking-submit-button"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-navy text-ivory px-9 py-4 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200 disabled:opacity-60 disabled:cursor-wait"
                  >
                    {submitting ? "Sending…" : "Send the inquiry"}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
